import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck2, CheckCircle2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const TOOTH_ICON_URL =
  "https://customer-assets.emergentagent.com/job_dr-valentina-dental/artifacts/y27aeqod_free-icon-tooth-extraction-2441173.png";
const BG_ICON_HEALTH =
  "https://customer-assets.emergentagent.com/job_dr-valentina-dental/artifacts/i4vqpnel_free-icon-health-13626974.png";
const BG_ICON_TOOTH =
  "https://customer-assets.emergentagent.com/job_dr-valentina-dental/artifacts/ikxde7zs_free-icon-tooth-11805479.png";

const CONTACT = {
  phone: "+380639768852",
};

const PRICE_SECTIONS = [
  {
    title: "1. ДІАГНОСТИКА ТА КОНСУЛЬТАЦІЯ",
    items: [
      {
        service: "Первинна консультація хірурга-стоматолога (огляд, збір анамнезу)",
        price: "500 грн",
      },
      {
        service: "Комплексна консультація з імплантації (аналіз КТ, складання плану лікування)",
        price: "800 грн",
      },
      {
        service: "Повторний огляд / Зняття швів (виконане в іншій клініці)",
        price: "300 грн",
      },
    ],
  },
  {
    title: "2. ВИДАЛЕННЯ ЗУБІВ (ДОРОСЛИЙ ПРИЙОМ)",
    items: [
      { service: "Видалення просте (рухомий зуб, однокореневий)", price: "1 000 грн" },
      {
        service: "Видалення багатокореневого зуба з сепарацією коренів",
        price: "1 500 - 2 500 грн",
      },
      {
        service:
          "Атравматичне видалення зуба зі збереженням параметрів альвеолярного гребеня (при одномоментній імплантації)",
        price: "2 000 - 2 500 грн",
      },
      {
        service: "Видалення ретинованого / дистопованого зуба (зуб мудрості)",
        price: "3 500 - 5 500 грн",
      },
      {
        service: "Консервація лунки після видалення (без вартості остеопластичного матеріалу)",
        price: "2 500 грн",
      },
      { service: "Кістектомія", price: "2 500 грн" },
    ],
  },
  {
    title: "3. ДИТЯЧА ХІРУРГІЯ",
    items: [
      {
        service: "Видалення тимчасового (молочного) зуба (просте/фізіологічне)",
        price: "1 000 грн",
      },
      {
        service: "Видалення тимчасового зуба (складне/періодонтит)",
        price: "1 500 грн",
      },
      {
        service: "Пластика вуздечки язика / губи (лазерна, апаратна методика)",
        price: "3 000 грн",
      },
      { service: "Циркумцизія (висічення капюшона при прорізуванні)", price: "1 000 грн" },
      { service: "Видалення зачатка зуба", price: "3 000 - 5 000 грн" },
    ],
  },
  {
    title: "4. ОРТОДОНТИЧНА ПІДГОТОВКА",
    items: [
      { service: "Розкриття іклів (для витяжіння брекет-системою)", price: "3 000 грн" },
      {
        service: "Видалення премолярів (за направленням ортодонта, за 1 од.)",
        price: "2 000 грн",
      },
    ],
  },
  {
    title: "5. ІМПЛАНТАЦІЯ ТА РЕКОНСТРУКЦІЯ",
    items: [
      { service: "Установка імплантату", price: "від 20 000 грн" },
      { service: "Розкриття імплантату", price: "2 000 грн" },
      { service: "Установка формувача ясен", price: "2 000 грн" },
      {
        service: "Закритий синус-ліфтинг (без вартості кісткового матеріалу)",
        price: "12 000 грн",
      },
      {
        service: "Відкритий синус-ліфтинг (без вартості кісткового матеріалу)",
        price: "28 000 грн",
      },
      { service: "Кісткова пластика (1 сегмент)", price: "від 7 000 грн" },
      { service: "М'якотканинна аугментація (1 сегмент)", price: "від 4 000 грн" },
      {
        service:
          "Забір слизового трансплантата (з горба верхньої щелепи або з піднебіння)",
        price: "1 500 грн",
      },
      { service: "Видалення імплантату", price: "4 000 грн" },
    ],
  },
  {
    title: "6. ДОДАТКОВІ МАНІПУЛЯЦІЇ",
    items: [
      { service: "Місцева анестезія (інфільтраційна / провідникова)", price: "350 грн" },
      { service: "Аплікаційна анестезія", price: "150 грн" },
      { service: "Накладання швів (1 одиниця)", price: "250 грн" },
      { service: "Зняття швів", price: "250 грн" },
      { service: "Медикаментозна обробка", price: "300 грн" },
      { service: "Використання мембрани", price: "від 2 000 грн" },
      { service: "Розкриття абсцессу, встановлення дренажу", price: "1 000 грн" },
    ],
  },
];

const BgIcon = ({ src, size = 24, className = "" }) => (
  <img
    src={src}
    alt=""
    width={size}
    height={size}
    className={className}
    style={{ objectFit: "contain" }}
  />
);

function PricePage() {
  useEffect(() => {
    const pageUrl = "https://dr-valentina-surgery.od.ua/prices";
    const title = "Ціни на стоматологічну хірургію в Одесі | Dr. Валентина Назарова";
    document.title = "Ціни на стоматологічну хірургію в Одесі | Dr. Валентина Назарова";

    const description =
      "Ціни на видалення зубів, імплантацію, синус-ліфтинг та дитячу хірургію в Одесі. Прайс хірурга-стоматолога Валентини Назарової.";

    const setMetaTag = (selector, attrName, attrValue, content) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", pageUrl);

    setMetaTag('meta[property="og:title"]', "property", "og:title", title);
    setMetaTag('meta[property="og:description"]', "property", "og:description", description);
    setMetaTag('meta[property="og:url"]', "property", "og:url", pageUrl);
    setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMetaTag('meta[name="twitter:description"]', "name", "twitter:description", description);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <BgIcon src={BG_ICON_HEALTH} className="absolute top-16 left-[4%] opacity-[0.04]" size={100} />
        <BgIcon src={BG_ICON_TOOTH} className="absolute top-[30%] right-[5%] opacity-[0.05] rotate-12" size={80} />
        <BgIcon src={BG_ICON_HEALTH} className="absolute bottom-[30%] left-[6%] opacity-[0.04] -rotate-12" size={90} />
        <BgIcon src={BG_ICON_TOOTH} className="absolute top-[55%] right-[15%] opacity-[0.03] rotate-45" size={70} />
      </div>

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={TOOTH_ICON_URL} alt="tooth" width={32} height={32} style={{ objectFit: "contain" }} />
              <span className="font-['Outfit'] text-base sm:text-xl font-semibold text-[#2a4a5a]">Dr. Назарова</span>
            </Link>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-[#c5ddd5] px-3 sm:px-4 py-2 text-sm font-medium text-[#2a4a5a] hover:bg-[#f0f8f6]"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">На головну</span>
                <span className="sm:hidden">Назад</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-16 sm:pb-20">
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8 sm:mb-12">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#5a9a8a] mb-3">м. Одеса, 2026 р.</p>
          <h1 className="font-['Outfit'] text-2xl sm:text-4xl lg:text-5xl font-semibold text-[#2a4a5a] tracking-tight leading-tight mb-4">
            ЦІНИ НА ХІРУРГІЧНУ СТОМАТОЛОГІЮ В ОДЕСІ
          </h1>
          <p className="text-[#6a8a9a] text-sm sm:text-base max-w-2xl leading-relaxed">
            Актуальний прайс на консультації, видалення зубів, імплантацію, синус-ліфтинг та дитячу хірургію в Одесі.
          </p>
          <p className="mt-3 text-[#6a8a9a] text-sm sm:text-base max-w-2xl leading-relaxed">
            Сторінка допомагає швидко порівняти вартість стоматологічних послуг в Одесі перед записом на прийом.
          </p>
        </motion.section>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-5">
          {PRICE_SECTIONS.map((section, sectionIndex) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: sectionIndex * 0.03 }}
              className="bg-[#f8fbfa] border border-[#dbeae5] rounded-2xl p-4 sm:p-6"
            >
              <h2 className="font-['Outfit'] text-lg sm:text-xl font-semibold text-[#2a4a5a] mb-4">{section.title}</h2>
              <ul className="space-y-3">
                {section.items.map((item) => (
                  <li key={`${section.title}-${item.service}`} className="rounded-xl bg-white border border-[#e5f0ec] p-3 sm:p-4">
                    <div className="flex gap-2 items-start">
                      <CheckCircle2 size={16} className="text-[#5a9a8a] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-[15px] leading-relaxed text-[#2a4a5a]">{item.service}</p>
                        <p className="mt-1 text-sm font-semibold text-[#4a8a7a]">{item.price}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.section>
          ))}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          className="mt-8 sm:mt-10 rounded-2xl sm:rounded-3xl bg-white border border-[#dbeae5] p-5 sm:p-7"
        >
          <p className="text-sm sm:text-base text-[#5f7f87] leading-relaxed">
            Ціни вказані з ознайомчою метою. Остаточна вартість формується після очного огляду та аналізу рентгенологічних знімків.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-[#5a9a8a] hover:bg-[#4a8a7a] text-white rounded-full px-6 py-5 text-sm">
              <Link to="/#booking">
                <CalendarCheck2 size={16} className="mr-2" />
                Записатись на консультацію
              </Link>
            </Button>
            <a
              href={`tel:${CONTACT.phone}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#c5ddd5] text-[#2a4a5a] hover:border-[#5a9a8a] hover:bg-[#f0f8f6] transition-all text-sm"
            >
              <Phone size={16} />
              <span>{CONTACT.phone}</span>
            </a>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          className="mt-8 rounded-2xl sm:rounded-3xl bg-[#f8fbfa] border border-[#dbeae5] p-5 sm:p-7"
        >
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-semibold text-[#2a4a5a] mb-4">
            Часті питання про ціни в Одесі
          </h2>
          <div className="space-y-4 text-[#5f7f87]">
            <div>
              <h3 className="font-semibold text-[#2a4a5a]">Яка ціна видалення зуба в Одесі?</h3>
              <p className="text-sm sm:text-base">Вартість залежить від складності: від простого видалення до ретинованих зубів мудрості.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#2a4a5a]">Скільки коштує імплантація зуба в Одесі?</h3>
              <p className="text-sm sm:text-base">У прайсі вказана базова ціна на встановлення імплантату та супутні хірургічні етапи.</p>
            </div>
            <div>
              <h3 className="font-semibold text-[#2a4a5a]">Чи змінюється фінальна вартість лікування?</h3>
              <p className="text-sm sm:text-base">Так, остаточний кошторис формується після огляду, КТ-аналізу та погодженого плану лікування.</p>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
}

export default PricePage;
